import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listRenewalPayments,
  getRenewalPayment,
  getRenewalShroffVoucherPdf,
  searchRenewalPayment,
  confirmRenewalManualPayment,
} from "../../services/renewalService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
} from "../../components/common/DashboardUI";
import ReceiptFileInput from "../../components/common/ReceiptFileInput";

// Membership renewal workflow (Request -> pay -> Active, new membership
// dates) — see MembershipRenewalController. Shroff/manual-pay only, like ID
// Card Reprints (IdCardReprints.jsx): the backend never builds a CyberExt
// payload for a renewal, so there is no online-payment path to wire up here
// — only a "Confirm Payment" tab, no separate approvals tab. Once a manual
// receipt is submitted it goes through the existing shared review queue on
// the Payments page's "Manual Approvals" tab (ManualPaymentApprovalController
// already special-cases this payable type), which is what actually flips the
// renewal to Paid and updates the member's dates — nothing to build here for
// that step.
//
// Two tabs, each independently permission-gated (module visibility itself is
// gated in adminNav.js by "view_payments"):
//   - Renewals        -> "view_payments" (list) / "view_payment_details" (the
//                         backend gates the single-record fetch on this; the
//                         frontend doesn't re-check it before opening the
//                         detail modal, same as PaymentsManagement.jsx's own
//                         PaymentDetailModal)
//   - Confirm Payment  -> the reference search box needs only "view_payments";
//     the "Submit Shroff Receipt" form underneath it additionally needs
//     "submit_manual_payment_receipts" (Instructor) — see ConfirmPaymentTab
//
// MembershipRenewalController::index/show build their response through the
// same formatRenewal() used for the member's own /member/renewals — which
// only whitelists a fixed set of process columns. The joined member
// name/email/nic and payment_status/process_status description strings
// index() fetches never make it into the JSON, so this page can only show
// member_code (not a name) and has to derive a Paid/Pending state from
// `paid_at` rather than trust a status string — a backend response-shape
// gap, not fixable here without touching the backend.
const isPaid = (r) => Boolean(r?.paid_at);

function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);
const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};
const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// ── Modal shell (matches the pattern used across the admin panel) ───────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker).
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── Renewals tab (browse the full ledger, view details, download voucher) ──
function RenewalsTab({ canViewVoucher }) {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // renewal_id of the open detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const fetchRenewals = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    listRenewalPayments(params)
      .then(setRenewals)
      .catch((err) => toast.error(errInfo(err, "Could not load membership renewals.").message))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchRenewals, 300);
    return () => clearTimeout(t);
  }, [fetchRenewals]);

  const openDetail = (renewalId) => {
    setSelected(renewalId);
    setDetail(null);
    setDetailLoading(true);
    getRenewalPayment(renewalId)
      .then(setDetail)
      .catch((err) => {
        toast.error(errInfo(err, "Could not load this renewal's details.").message);
        setSelected(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const { page, setPage, totalPages, pageItems: pagedRenewals } = usePagination(renewals, { resetKey: search });

  const handleDownloadVoucher = async () => {
    if (!detail || downloadingVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getRenewalShroffVoucherPdf(detail.renewal_id);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not load the shroff voucher.").message);
    } finally {
      setDownloadingVoucher(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by member code, name, email, NIC or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : renewals.length === 0 ? (
          <EmptyState icon={ICONS.refresh} text="No membership renewals match your search." />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[820px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Member</th>
                  <th className="py-2 px-3 font-medium">Reference</th>
                  <th className="py-2 px-3 font-medium">Old End Date</th>
                  <th className="py-2 px-3 font-medium">New End Date</th>
                  <th className="py-2 px-3 font-medium">Payable</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedRenewals.map((r) => (
                  <tr key={r.renewal_id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                      {r.member_code}
                    </td>
                    <td className="py-3 px-3 text-xs font-mono text-gray-600 whitespace-nowrap">{r.payment_reference}</td>
                    <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.old_membership_end_date)}</td>
                    <td className="py-3 px-3 text-xs text-gray-600 whitespace-nowrap">{fmtDate(r.new_membership_end_date)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(r.payable_amount)}</td>
                    <td className="py-3 px-3">
                      <Pill>{isPaid(r) ? "Paid" : "Pending"}</Pill>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => openDetail(r.renewal_id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {selected && (
        <Modal title="Membership Renewal" onClose={closeDetail} size="lg">
          {detailLoading || !detail ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                    {detail.member_code}
                  </p>
                  <p className="text-xs text-gray-400 truncate">Renewal #{detail.renewal_id}</p>
                </div>
                <Pill>{isPaid(detail) ? "Paid" : "Pending"}</Pill>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  ["Payment reference", detail.external_payment_reference],
                  ["Payable amount", money(detail.payable_amount)],
                  ["Paid amount", Number(detail.paid_amount) > 0 ? money(detail.paid_amount) : null],
                  ["Old membership start", fmtDate(detail.old_membership_start_date)],
                  ["Old membership end", fmtDate(detail.old_membership_end_date)],
                  ["New membership start", fmtDate(detail.new_membership_start_date)],
                  ["New membership end", fmtDate(detail.new_membership_end_date)],
                  ["Paid at", detail.paid_at ? fmtDateTime(detail.paid_at) : null],
                  ["Requested at", fmtDateTime(detail.created_at)],
                ]
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-xs text-gray-400">{label}</dt>
                      <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                        {value}
                      </dd>
                    </div>
                  ))}
              </dl>

              {canViewVoucher && !isPaid(detail) && (
                <button
                  type="button"
                  onClick={handleDownloadVoucher}
                  disabled={downloadingVoucher}
                  className={`${btnSecondary} !w-auto px-4 inline-flex items-center gap-2`}
                >
                  <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                  {downloadingVoucher ? "Opening…" : "Download Shroff Voucher"}
                </button>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Confirm Payment tab (shroff/manual — the only payment path a renewal fee
// can actually be marked Paid through today). Search-then-confirm, mirroring
// MembersManagement.jsx's ConfirmShroffPaymentTab: look the renewal up by its
// voucher reference first (surfacing payer name + payable amount so the
// operator can verify it's the right person before taking money), then
// submit the receipt. ────────────────────────────────────────────────────
function ConfirmPaymentTab({ canConfirm }) {
  const [reference, setReference] = useState("");
  const [searching, setSearching] = useState(false);
  const [payment, setPayment] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);

  const fieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const resetConfirmForm = (p) => {
    // Left blank on purpose — the operator types the amount actually received.
    setPaidAmount("");
    setPaidDate(new Date().toISOString().slice(0, 10));
    setReceiptReference("");
    setReceiptFile(null);
    setNote("");
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirmResult(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || searching) return;
    setSearching(true);
    setSearchError("");
    setPayment(null);
    setConfirmResult(null);
    try {
      const found = await searchRenewalPayment(ref);
      if (!found) {
        setSearchError("No membership renewal payment found for that reference.");
        return;
      }
      setPayment(found);
      resetConfirmForm(found);
    } catch (err) {
      setSearchError(errInfo(err, "No membership renewal payment found for that reference.").message);
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !payment || !receiptFile) return;
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirming(true);
    try {
      const r = await confirmRenewalManualPayment({
        payment_reference: payment.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference.trim(),
        receipt: receiptFile,
        note: note.trim() || undefined,
      });
      toast.success(r.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setConfirmResult(r);
    } catch (err) {
      const info = errInfo(err, "Could not submit this receipt.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  const isPending = payment?.payment_status === "Pending";

  return (
    <div className="max-w-xl space-y-4">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
          Find a membership renewal payment
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Enter the reference number printed on the member's shroff voucher, then record the payment taken at the
          counter.
        </p>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <input
            className={`${inputClass} flex-1 min-w-[220px] font-mono`}
            placeholder="Reference number, e.g. 07304000002"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <button type="submit" disabled={searching || !reference.trim()} className={`${btnPrimary} !w-auto px-6`}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
      </div>

      {payment && (
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {payment.payer_name || payment.payer_code}
              </p>
              <p className="text-xs text-gray-400 truncate">{payment.payer_email}</p>
            </div>
            <Pill>{payment.payment_status}</Pill>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {[
              ["Payment reference", payment.payment_reference],
              ["Payable amount", money(payment.payable_amount)],
              ["Paid amount", Number(payment.paid_amount) > 0 ? money(payment.paid_amount) : null],
              ["Paid at", payment.paid_at ? fmtDateTime(payment.paid_at) : null],
            ]
              .filter(([, v]) => v !== null && v !== undefined && v !== "")
              .map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-gray-400">{label}</dt>
                  <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                    {value}
                  </dd>
                </div>
              ))}
          </dl>

          {!isPending ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              This payment is already <strong>{payment.payment_status}</strong> — nothing to confirm.
            </p>
          ) : !canConfirm ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              You don't have permission to confirm this payment.
            </p>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                Submit Shroff Receipt
              </h4>
              <p className="text-xs text-gray-400 -mt-2">
                Submitting confirms this payment immediately — the member's membership is extended right away. A
                Director/Admin will acknowledge the receipt afterward for audit purposes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    required
                  />
                  {fieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_amount")}</p>}
                </div>
                <div>
                  <label className={labelClass}>Paid date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    required
                  />
                  {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
                </div>
              </div>

              <div>
                <label className={labelClass}>Receipt / slip reference</label>
                <input
                  className={inputClass}
                  value={receiptReference}
                  onChange={(e) => setReceiptReference(e.target.value)}
                  required
                />
                {fieldError("receipt_reference") && <p className="text-xs text-red-600 mt-1">{fieldError("receipt_reference")}</p>}
              </div>

              <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={fieldError("receipt")} />

              <div>
                <label className={labelClass}>
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea className={`${inputClass} resize-none`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                {fieldError("note") && <p className="text-xs text-red-600 mt-1">{fieldError("note")}</p>}
              </div>

              {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

              <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} w-full`}>
                {confirming ? "Confirming…" : "Confirm Payment"}
              </button>
            </form>
          )}

          {confirmResult && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {confirmResult.message || "Payment confirmed. Receipt pending director acknowledgement."}{" "}
              {confirmResult.approval_id && (
                <>
                  Approval ID: <span className="font-mono font-semibold">#{confirmResult.approval_id}</span> — will be
                  acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function MembershipRenewals() {
  const { can } = useAuth();
  const canViewRenewals = can("view_payments");
  // "confirm_shroff_payments" (e.g. the Shroff role) is accepted by the
  // backend's */manual-confirm routes too, so it also unlocks this tab.
  const canConfirmPayment = can(["submit_manual_payment_receipts", "confirm_shroff_payments"]);
  const canViewVoucher = can("view_payments");

  const tabs = [
    canViewRenewals && { key: "renewals", label: "Renewals" },
    (canConfirmPayment || canViewVoucher) && { key: "confirm", label: "Confirm Payment" },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState(tabs[0]?.key || "renewals");

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Membership Renewals
        </h1>
        <p className="text-sm text-gray-500">Renewal requests and counter payment confirmation.</p>
      </div>

      {tabs.length === 0 ? (
        <EmptyState icon={ICONS.refresh} text="You don't have permission to access any part of this module." />
      ) : (
        <>
          {tabs.length > 1 && (
            <div className="flex gap-1 mb-5 border-b border-gray-200">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
                    activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === "renewals" && canViewRenewals && <RenewalsTab canViewVoucher={canViewVoucher} />}
          {activeTab === "confirm" && (canConfirmPayment || canViewVoucher) && (
            <ConfirmPaymentTab canConfirm={canConfirmPayment} />
          )}
        </>
      )}
    </div>
  );
}
